<template>
    <button
        type="button"
        class="btn btn-secondary btn-block"
        :disabled="loading"
        @click="onSendMagicLinkClick()"
    >
        <span v-if="loading" class="loading" />
        <span v-text="loading ? $t('customer.loginForm.loading') : $t('customer.loginForm.sendMagicLink')" />
    </button>
</template>

<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

const { t } = useI18n()

const email = defineModel<string>('email', { default: '' })

const props = defineProps<{
    /**
     * Checked before the request goes out. The caller reports the problem on the field
     * itself, so this button stays out of the surrounding form's own validation.
     */
    validate: () => Promise<boolean>
}>()

const { showNotification } = useNotification()
const { apiClient } = useShopwareContext()
const { recaptcha } = useRecaptchaV3()
const { saveWishlistToLocal } = useMagicLinkCart()

const loading = ref(false)

async function onSendMagicLinkClick () {
    try {
        loading.value = true

        const isValid = await props.validate()

        if (!isValid) {
            loading.value = false
            return
        }

        const isNotABot = await recaptcha()

        if (!isNotABot) {
            showNotification('Recaptcha failed, please try again.', 'error', true)
            loading.value = false
            return
        }

        // Save current wishlist to localStorage before sending magic link
        saveWishlistToLocal()

        // Call the store-api endpoint for magic link generation
        await apiClient.invoke('generateMagicLink post /account/login/magic-link/generate', {
            body: {
                email: email.value
            }
        })

        showNotification(t('customer.loginForm.magicLinkSent'))

        loading.value = false
    } catch (e: unknown) {
        console.error('Error in onSendMagicLinkClick:', e)
        loading.value = false

        if (e instanceof ApiClientError) {
            const detail = e.details?.errors?.[0]?.detail
            showNotification(detail ?? e.message, 'error', true)
        } else {
            showNotification(e instanceof Error ? e.message : String(e), 'error', true)
        }
    }
}
</script>
